import React from "react";
import {
  FlagCountResponse,
  GroupCount,
  ItemWithMetaData,
  TopicCount,
} from "../../api/Items";
import { behaviorRelabelMapping, useFamaFlags } from "../../api/FamaFlags";
import { useLensTopicsEnabled } from "./useLensTopicsEnabled";

interface SummaryFlagTextProps {
  profileList: ItemWithMetaData[] | undefined;
  donutChartValues: { label: string; value: number }[];
  keywordCount: GroupCount | undefined;
  topicCount?: TopicCount;
  flagsCount: FlagCountResponse | undefined;
}

function plural(
  count: number,
  singular: string,
  pluralForm: string
): string {
  return count === 1 ? singular : pluralForm;
}

function SummaryFlagTextRedesign({
  profileList,
  keywordCount,
  topicCount,
  flagsCount,
  includeTopics,
}: Omit<SummaryFlagTextProps, "donutChartValues"> & {
  includeTopics: boolean;
}) {
  const profileCount = profileList?.length ?? 0;
  const postsCount =
    flagsCount?.item_types.groups.find(
      (group) => group.name === "social_media_post"
    )?.count ?? 0;
  const keywordTotal = keywordCount?.total ?? 0;
  const originalTotal =
    flagsCount?.post_types.groups.find((group) => group.name === "original")
      ?.count ?? 0;
  const likedTotal =
    flagsCount?.post_types.groups.find((group) => group.name === "liked")
      ?.count ?? 0;
  const sharedTotal =
    flagsCount?.post_types.groups.find((group) => group.name === "shared")
      ?.count ?? 0;
  const articlesCount =
    flagsCount?.item_types.groups.find(
      (group) => group.name === "web_content"
    )?.count ?? 0;
  const topicsCount = topicCount?.total ?? 0;

  return (
    <div className="text-lg text-gray-600">
      The subject has
      <span className="font-bold mx-1">{profileCount}</span>
      social media {plural(profileCount, "profile", "profiles")}.
      <span className="font-bold mx-1">{postsCount}</span>
      social media {plural(postsCount, "post was", "posts were")} flagged{includeTopics ? "," : ""} and
      <span className="font-bold mx-1">{keywordTotal}</span>
      {plural(keywordTotal, "keyword was", "keywords were")} found.
      {includeTopics ? (
        <>
          <span className="font-bold mx-1">{topicsCount}</span>
          {plural(topicsCount, "topic was", "topics were")} tagged. Of those,
        </>
      ) : (
        " Of those,"
      )}
      <span className="font-bold mx-1">{originalTotal}</span>
      {plural(originalTotal, "was", "were")} originally posted by the subject,
      <span className="font-bold mx-1">{likedTotal}</span>
      {plural(likedTotal, "was", "were")} liked, and
      <span className="font-bold mx-1">{sharedTotal}</span>
      {plural(sharedTotal, "was", "were")} shared.
      <span className="font-bold mx-1">{articlesCount}</span>
      relevant {plural(articlesCount, "article was", "articles were")} found.
      <span className="ml-1">
        Posts and articles that received these flags are detailed in the "Posts
        and Articles" section.
      </span>
    </div>
  );
}

export function SummaryFlagText({
  profileList,
  donutChartValues,
  keywordCount,
  topicCount,
  flagsCount,
}: SummaryFlagTextProps) {
  const { behaviorMappingActions, v3UiReportSummaryInsightRedesign } =
    useFamaFlags();
  const lensTopicsEnabled = useLensTopicsEnabled();

  if (lensTopicsEnabled || v3UiReportSummaryInsightRedesign) {
    return (
      <SummaryFlagTextRedesign
        profileList={profileList}
        keywordCount={keywordCount}
        topicCount={topicCount}
        flagsCount={flagsCount}
        includeTopics={lensTopicsEnabled}
      />
    );
  }

  const postsCount =
    flagsCount?.item_types.groups.find(
      (group) => group.name === "social_media_post"
    )?.count ?? 0;
  const keywordTotal = keywordCount?.total ?? 0;
  const originalTotal =
    flagsCount?.post_types.groups.find((group) => group.name === "original")
      ?.count ?? 0;
  const likedTotal =
    flagsCount?.post_types.groups.find((group) => group.name === "liked")
      ?.count ?? 0;
  const sharedTotal =
    flagsCount?.post_types.groups.find((group) => group.name === "shared")
      ?.count ?? 0;

  const behaviorsNames =
    behaviorRelabelMapping(
      flagsCount?.behaviors.groups.map((group) => group.name) ?? [],
      behaviorMappingActions
    ).map((name) =>
      name.toLowerCase().replace(/\b(\w)/g, (s) => s.toUpperCase())
    ) ?? [];

  if (flagsCount?.behaviors.total === 0 && keywordTotal === 0) {
    return <></>;
  }

  return (
    <div className="text-lg text-gray-600">
      The subject has
      <span className="font-bold mx-1">{profileList?.length ?? 0}</span> social
      media profiles.
      <span className="font-bold mx-1">{postsCount}</span> social media posts
      were flagged and
      <span className="font-bold mx-1">{keywordTotal}</span>
      keywords were found. Of those,
      <span className="font-bold mx-1">{originalTotal}</span>
      were originally posted by the subject,
      <span className="font-bold mx-1">{likedTotal}</span>
      were liked, and <span className="font-bold mx-1">{sharedTotal}</span>
      were shared.
      {behaviorsNames.length > 0 && (
        <span className="ml-1">
          Content was flagged for
          {behaviorsNames.length === 1 && (
            <>
              <span className="font-bold ml-1">{behaviorsNames[0]}</span>
            </>
          )}
          {behaviorsNames.length > 1 && (
            <>
              {behaviorsNames.map((name, index) => {
                return (
                  <React.Fragment key={index}>
                    <span className="font-bold ml-1">{name}</span>
                    {index < behaviorsNames.length - 2 && ", "}
                    {index === behaviorsNames.length - 2 && " and "}
                  </React.Fragment>
                );
              })}
            </>
          )}
          .
        </span>
      )}
      <span className="ml-1">
        Posts and articles that received these flags are detailed in the "Posts
        and Articles" section.
      </span>
    </div>
  );
}
